import { defineStore } from 'pinia'
import { router } from '@/router'
import Network from '@/Network'
import {
  ConsultingResponse,
  ListStationsResponse,
  StationsToApproveResponse,
  type BulkSectorSpecialtyResponse,
  type MoveStationsResponse
} from '@/Network/Types/Responses/StationResponses'
import {
  ConsultingRequest,
  DashboardType,
  ListFiltersRequest,
  type BulkSectorSpecialtyRequest
} from '@/Network/Types/Requests/StationRequests'
import { startOfWeek, endOfWeek } from 'date-fns'
import { FilterDashboardStates } from '@/components/Dashboard/Header/components/useFilterDashboard'
import { getApiErrorMessage } from '@/utils/apiError'
import { clearFilterDashboardOpt } from '@/components/Dashboard/Header/components/useFilterDashboard'
import { mergeMoreFiltersIntoQuery } from '@/components/Dashboard/Header/components/moreFiltersQuery'
import type { LocationQueryRaw } from 'vue-router'
import { useDashboardLensStore } from '@/pinia-store/useDashboardLensStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { serializeLensQuery } from '@/modules/company/Dashboard/lenses/lensState'
import {
  MONTH_SCHEDULES_BATCH,
  trimListToDays,
  withInclusiveEndDate
} from '@/modules/company/Dashboard/lenses/dashboardQuery'
import { countSchedules, mergeStationTrees } from '@/modules/company/Dashboard/lenses/gridModel'

export enum FiltersType {
  DATE = 'DATE'
}

type DateConfig = {
  startDate: Date
  endDate: Date
}

export type StationState = {
  listStations: ListStationsResponse | undefined
  listStationsQuery: ListFiltersRequest
  consulting: ConsultingResponse
  isLoadingListStations: boolean
  /** Erro do load da grade de plantões (getListStations) — null quando ok. */
  loadError: string | null
  /** Erro do load da lista de consulta (getListConsultingStations) — null quando ok. */
  consultingLoadError: string | null
  /** Total de escalas do filtro (Mês paginado); null = BE não informou. */
  totalSchedules: number | null
  isLoadingMore: boolean
  /**
   * Erro do último "Carregar mais" (loadMoreSchedules) — null quando ok. Separado de
   * `loadError` para a grade já carregada continuar visível; exibido junto do botão.
   */
  loadMoreError: string | null
  /** false no Calendário: a grade não é buscada enquanto ele está aberto. */
  gridFetchEnabled: boolean
  /**
   * Offset do próximo "Carregar mais" depois do 1º lote extra (null = ainda não houve lote:
   * usa as escalas carregadas). O BE pagina pela lista de escalas do escopo, não pelas que
   * vieram com plantão — com filtro de médico/vaga/horário as duas diferem (F3).
   */
  schedulesCursor: number | null
}

export const STATIONS_LOAD_ERROR = 'Não foi possível carregar os plantões'
export const STATIONS_LOAD_MORE_ERROR = 'Não foi possível carregar mais escalas'
// Mesmo texto do fallback do StationService.getConsultingList: quando o funil de
// erro devolve o genérico, o consumidor deduplica e não repete como detalhe.
export const CONSULTING_LOAD_ERROR = 'Não foi possível carregar a lista de plantões'

let _fetchId = 0
/** `@Max(200)` do `schedulesLimit` no BE (FiltersStationsDto). */
const MAX_SCHEDULES_LIMIT = 200
// Token do lote em voo: só o lote corrente desliga `isLoadingMore` / grava `loadMoreError`.
let _loadMoreId = 0
let _consultingFetchId = 0

/** Cada store com a própria lista quando a resposta é compartilhada (updateAllStores). */
function copyConsulting(data: ConsultingResponse): ConsultingResponse {
  return data && Array.isArray(data.rows) ? { ...data, rows: [...data.rows] } : data
}

const schedulesBatchSize = (query: ListFiltersRequest): number =>
  Number(query.schedulesLimit ?? MONTH_SCHEDULES_BATCH)

/**
 * Query da recarga silenciosa: pede de uma vez tudo o que os lotes já cobriram (cursor), não
 * só as escalas visíveis — com filtro de médico/vaga as duas diferem e a grade encolheria.
 */
function buildRefreshQuery(
  base: ListFiltersRequest,
  covered: number,
  flat: boolean
): ListFiltersRequest {
  const query = { ...base }
  if (query.schedulesLimit) {
    const limit = Math.max(Number(query.schedulesLimit), covered)
    query.schedulesLimit = String(Math.min(limit, MAX_SCHEDULES_LIMIT))
  }
  if (flat) query.schedulesOffset = '0'
  return query
}

/**
 * Mês V2 com filtro que o `totalSchedules` não conta: a 1ª página já vai com
 * `schedulesOffset=0`. Sem offset o BE devolve unidades completas (nº de escalas
 * desconhecido para o front, e a árvore só traz as que têm plantão no filtro); com offset
 * ele fatia plano `[offset, offset + limite)` da lista do escopo e o cursor fica exato.
 */
function usesFlatPaging(query: ListFiltersRequest, totalIsExact: boolean): boolean {
  return (
    !totalIsExact &&
    !!query.schedulesLimit &&
    query.type === DashboardType.MONTHLY &&
    !!useFeatureFlags().flags.enabledDashboardV2
  )
}

/**
 * Filtros que entram na grade mas NÃO no `totalSchedules` do BE
 * (`findLimitedScheduleIdsByHealthUnits` × `applyStationScheduleFilters`/`applyStationDoctorFilters`).
 * Pagamento também filtra a grade: `applyStationDoctorFilters` chama
 * `applyStationPaymentFilters` (station.typeorm.repository.ts:3029, :3094–3236: `inCash`,
 * `advancePaid`, `advanceRequested`, `paymentValueMin/Max`, `ourTeam`, `status`); `valueMin/Max`
 * são aplicados no service (stations.service.ts:3989).
 */
const TOTAL_SCHEDULES_NOT_COUNTED = [
  'startHour',
  'endHour',
  'doctorIds',
  'doctorPending',
  'openedStation',
  // Chips M10-A: filtram os plantões da grade, não as escalas do total.
  'coverageStatus',
  'stationType',
  'inCash',
  'advanceRequested',
  'advancePaid',
  'ourTeam',
  'status',
  'valueMin',
  'valueMax',
  'paymentValueMin',
  'paymentValueMax'
] as const satisfies readonly (keyof ListFiltersRequest)[]

/**
 * `GET /stations` da grade. No Mês da V2 o fim vai até o dia seguinte e a coluna extra é
 * cortada (F3: plantões das 21h–23h59 do último dia — ver `withInclusiveEndDate`). Semana
 * e flag desligada: requisição da base.
 */
async function fetchGridList(query: ListFiltersRequest): Promise<ListStationsResponse> {
  if (!useFeatureFlags().flags.enabledDashboardV2 || query.type !== DashboardType.MONTHLY) {
    return Network.station.list(query)
  }
  const { query: extended, days } = withInclusiveEndDate(query)
  const data = await Network.station.list(extended)
  return days === null ? data : trimListToDays(data, days)
}

export const useStationStore = defineStore('station', {
  state: (): StationState => ({
    listStations: undefined,
    listStationsQuery: {
      type: DashboardType.WEEK,
      startDate: startOfWeek(new Date()).toISOString(),
      endDate: endOfWeek(new Date()).toISOString(),
      startHour: '',
      endHour: '',
      healthUnits: [],
      schedules: []
    },
    consulting: {
      totalRecords: 0,
      rows: []
    },
    isLoadingListStations: false,
    loadError: null,
    consultingLoadError: null,
    totalSchedules: null,
    isLoadingMore: false,
    loadMoreError: null,
    gridFetchEnabled: true,
    schedulesCursor: null
  }),
  getters: {
    // eslint-disable-next-line complexity
    hasActiveFilters: (state): boolean => {
      const q = state.listStationsQuery
      return (
        (q.healthUnits?.length ?? 0) > 0 ||
        (q.schedules?.length ?? 0) > 0 ||
        !!q.startHour ||
        q.doctorIds !== undefined ||
        q.valueMin !== undefined ||
        q.valueMax !== undefined ||
        q.withCheckin !== undefined ||
        q.withCheckout !== undefined ||
        q.stationType !== undefined ||
        (q.sectorIds?.length ?? 0) > 0 ||
        (q.specialtyIds?.length ?? 0) > 0 ||
        (q.companySpecialtyIds?.length ?? 0) > 0 ||
        q.withoutSector !== undefined ||
        q.withoutSpecialty !== undefined
      )
    },
    loadedSchedulesCount: (state): number => countSchedules(state.listStations),
    /** Offset que o próximo "Carregar mais" vai pedir; ≥ `totalSchedules` = acabou. */
    nextSchedulesOffset: (state): number =>
      state.schedulesCursor ?? countSchedules(state.listStations),
    /**
     * F3 (nota do BE): `totalSchedules` conta as escalas do escopo (unidade, data, grupo,
     * setor/especialidade) e ignora os filtros por plantão (médico, horário, vaga, tipo,
     * pagamento). Com algum deles ativo, o total não é o número de escalas que a grade mostra.
     */
    totalSchedulesIsExact: (state): boolean =>
      !TOTAL_SCHEDULES_NOT_COUNTED.some((key) => {
        const value = state.listStationsQuery[key]
        if (Array.isArray(value)) return value.length > 0
        return value !== undefined && value !== null && value !== ''
      })
  },
  actions: {
    setConsulting(consulting: ConsultingResponse) {
      this.consulting = consulting
    },
    /**
     * Zera a lista de consulta sem passar pela API — usado quando a tela sabe de
     * antemao que a consulta e invalida (ex.: sem periodo). Incrementa o fetchId
     * para que uma requisicao em voo nao repovoe a lista depois.
     */
    clearConsulting() {
      _consultingFetchId++
      this.consulting = { rows: [], totalRecords: 0 }
      this.consultingLoadError = null
    },
    /**
     * @param shared request já disparado com os mesmos parâmetros (updateAllStores reaproveita o
     * GET /stations/consulting do relatório de médicos em vez de repeti-lo).
     */
    async getListConsultingStations(
      consultingQuery: ConsultingRequest,
      shared?: Promise<ConsultingResponse>
    ): Promise<void> {
      const fetchId = ++_consultingFetchId
      try {
        this.consultingLoadError = null
        const data = copyConsulting(
          await (shared ?? Network.station.getConsultingList(consultingQuery))
        )
        if (fetchId === _consultingFetchId) {
          this.consulting = data
        }
      } catch (error) {
        // fallback intencional: sem rethrow — a action é disparada em paralelo pelo
        // Promise.allSettled de updateAllStores (useFilterStation) e pelo loadItems
        // do Consult sem catch. O erro vira estado visível (consultingLoadError) e a
        // lista é zerada para o usuário não agir (bulk edit/remoção) sobre dados
        // obsoletos de uma consulta anterior.
        if (fetchId === _consultingFetchId) {
          this.consulting = { rows: [], totalRecords: 0 }
          this.consultingLoadError = getApiErrorMessage(error, CONSULTING_LOAD_ERROR)
        }
      }
    },
    async getListStations(): Promise<void> {
      if (!this.gridFetchEnabled) return
      const fetchId = ++_fetchId
      // Novo load principal invalida o lote em voo e libera o "Carregar mais" da grade nova.
      _loadMoreId++
      this.isLoadingMore = false
      this.loadMoreError = null
      this.schedulesCursor = null
      const flat = usesFlatPaging(this.listStationsQuery, this.totalSchedulesIsExact)
      const query = flat
        ? { ...this.listStationsQuery, schedulesOffset: '0' }
        : this.listStationsQuery
      try {
        this.isLoadingListStations = true
        this.loadError = null
        const data = await fetchGridList(query)
        if (fetchId === _fetchId) {
          this.listStations = data
          this.schedulesCursor = flat ? schedulesBatchSize(query) : null
          this.totalSchedules =
            typeof data?.totalSchedules === 'number' ? data.totalSchedules : null
        }
      } catch (error) {
        // fallback intencional: sem rethrow — a action é disparada por dezenas de
        // callers sem catch (filtros do Header, modais pós-save). O estado de erro
        // visível fica em loadError e a Dashboard renderiza ReportErrorState+retry,
        // distinto de grade vazia ("sem plantões").
        if (fetchId === _fetchId) {
          this.loadError = getApiErrorMessage(error, STATIONS_LOAD_ERROR)
        }
      } finally {
        if (fetchId === _fetchId) {
          this.isLoadingListStations = false
        }
      }
    },
    /**
     * Recarga silenciosa depois de gravar na grade (lote H7 / mover H8): não liga
     * `isLoadingListStations`, então a grade continua montada (sem skeleton, sem perder a
     * rolagem do Contínuo) até a resposta chegar. Pede de uma vez o mesmo número de escalas
     * que o "Carregar mais" já trouxe, sem mexer na query de paginação do usuário.
     * Sem dados, com erro na tela ou com load principal em voo → load normal.
     */
    async refreshListStations(): Promise<void> {
      if (!this.gridFetchEnabled) return
      if (!this.listStations || this.loadError || this.isLoadingListStations) {
        await this.getListStations()
        return
      }
      const fetchId = ++_fetchId
      _loadMoreId++
      this.isLoadingMore = false
      this.loadMoreError = null
      const flat = usesFlatPaging(this.listStationsQuery, this.totalSchedulesIsExact)
      const query = buildRefreshQuery(this.listStationsQuery, this.nextSchedulesOffset, flat)
      try {
        const data = await fetchGridList(query)
        if (fetchId === _fetchId) {
          this.listStations = data
          this.schedulesCursor = flat ? schedulesBatchSize(query) : null
          this.totalSchedules =
            typeof data?.totalSchedules === 'number' ? data.totalSchedules : null
        }
      } catch (error) {
        // Mesmo contrato do getListStations: sem rethrow (a gravação já deu certo e o modal
        // já avisou); a grade antiga não pode passar por atual → estado de erro com retry.
        if (fetchId === _fetchId) {
          this.loadError = getApiErrorMessage(error, STATIONS_LOAD_ERROR)
        }
      }
    },
    /**
     * Próximo lote de escalas do Mês. O lote só é mesclado se a grade não foi
     * recarregada (troca de período/filtro) enquanto ele estava em voo.
     */
    async loadMoreSchedules(): Promise<void> {
      if (!this.listStations || this.isLoadingMore) return
      const baseFetchId = _fetchId
      const baseList = this.listStations
      const loadMoreId = ++_loadMoreId
      this.isLoadingMore = true
      this.loadMoreError = null
      const offset = this.nextSchedulesOffset
      try {
        const extra = await fetchGridList({
          ...this.listStationsQuery,
          schedulesOffset: String(offset)
        })
        // Grade recarregada no meio (filtro/período): o lote é de outra consulta.
        if (baseFetchId !== _fetchId || this.listStations !== baseList) return
        this.listStations = mergeStationTrees(baseList, extra)
        // O BE devolve as escalas [offset, offset + limite) do escopo (mesmo as sem plantão
        // no filtro): o próximo lote começa depois delas.
        this.schedulesCursor = offset + schedulesBatchSize(this.listStationsQuery)
        if (typeof extra?.totalSchedules === 'number') this.totalSchedules = extra.totalSchedules
      } catch (error) {
        // fallback intencional: sem rethrow — o botão "Carregar mais" não tem catch.
        // O erro vira estado visível próprio (loadMoreError), sem derrubar a grade
        // já carregada, e só se ainda for o lote corrente.
        if (loadMoreId === _loadMoreId && baseFetchId === _fetchId) {
          this.loadMoreError = getApiErrorMessage(error, STATIONS_LOAD_MORE_ERROR)
        }
      } finally {
        if (loadMoreId === _loadMoreId) this.isLoadingMore = false
      }
    },
    setQueryString() {
      const query: Record<string, unknown> = { ...this.listStationsQuery }
      if (!query.startHour) delete query.startHour
      if (!query.endHour) delete query.endHour
      // Dashboard legada (flag OFF): URL idêntica à de antes — sem chaves de lente.
      if (!useFeatureFlags().flags.enabledDashboardV2) {
        router.replace({ query: query as LocationQueryRaw })
        return
      }
      // Paginação é estado de sessão, não de link compartilhável.
      delete query.schedulesLimit
      delete query.schedulesOffset
      const lensQuery = serializeLensQuery(useDashboardLensStore().lens)
      router.replace({ query: { ...lensQuery, ...query } as LocationQueryRaw })
    },
    async setDefaultQuery(options: { keepPeriod?: boolean } = {}) {
      const previous = this.listStationsQuery
      const keep = !!options.keepPeriod
      this.listStationsQuery = {
        type: keep ? previous.type : DashboardType.WEEK,
        startDate: keep ? previous.startDate : startOfWeek(new Date()).toISOString(),
        endDate: keep ? previous.endDate : endOfWeek(new Date()).toISOString(),
        ...(keep && previous.schedulesLimit ? { schedulesLimit: previous.schedulesLimit } : {}),
        startHour: undefined,
        endHour: undefined,
        healthUnits: [],
        schedules: [],
        doctorIds: undefined,
        valueMin: undefined,
        valueMax: undefined,
        paymentValueMin: undefined,
        paymentValueMax: undefined,
        withCheckin: undefined,
        withCheckout: undefined,
        stationType: undefined,
        inCash: undefined,
        advanceRequested: undefined,
        advancePaid: undefined,
        ourTeam: undefined,
        status: undefined,
        openedStation: undefined,
        // `coverageStatus` fica de fora de propósito: o objeto é novo (não herda a query anterior)
        // e a chave a mais quebraria o "flag OFF = chaves da base" (A11).
        operationalGroups: undefined,
        // Chaves de Setor/Especialidade só existem na Dashboard V2 (flag OFF = query da base).
        ...(useFeatureFlags().flags.enabledDashboardV2
          ? {
              sectorIds: undefined,
              specialtyIds: undefined,
              companySpecialtyIds: undefined,
              withoutSector: undefined,
              withoutSpecialty: undefined
            }
          : {})
      }
      clearFilterDashboardOpt()
      this.setQueryString()
      await this.getListStations()
    },
    async setDateFilter(config: DateConfig) {
      this.listStationsQuery.startDate = config.startDate.toISOString()
      this.listStationsQuery.endDate = config.endDate.toISOString()
      this.setQueryString()
      await this.getListStations()
    },
    /**
     * Troca o período (Semana/Mês). Datas em ISO do instante local, como o resto da
     * Dashboard: o BE compara `station.date::date >= startDate::date` e `< endDate::date`,
     * e o fim (23:59:59.999 local do domingo) em UTC cai no dia seguinte — o que
     * inclui o domingo no filtro exclusivo.
     */
    async setPeriodRange(config: DateConfig & { type: DashboardType }) {
      this.listStationsQuery.type = config.type
      this.listStationsQuery.startDate = config.startDate.toISOString()
      this.listStationsQuery.endDate = config.endDate.toISOString()
      delete this.listStationsQuery.schedulesOffset
      if (config.type === DashboardType.MONTHLY) {
        this.listStationsQuery.schedulesLimit = String(MONTH_SCHEDULES_BATCH)
      } else {
        delete this.listStationsQuery.schedulesLimit
      }
      this.setQueryString()
      await this.getListStations()
    },
    async setSectorFilter(filter: { sectorIds: string[]; withoutSector: boolean }) {
      this.listStationsQuery.sectorIds = filter.sectorIds.length ? filter.sectorIds : undefined
      this.listStationsQuery.withoutSector = filter.withoutSector ? 'true' : undefined
      this.setQueryString()
      await this.getListStations()
    },
    async setSpecialtyFilter(filter: {
      specialtyIds: string[]
      companySpecialtyIds: string[]
      withoutSpecialty: boolean
    }) {
      this.listStationsQuery.specialtyIds = filter.specialtyIds.length
        ? filter.specialtyIds
        : undefined
      this.listStationsQuery.companySpecialtyIds = filter.companySpecialtyIds.length
        ? filter.companySpecialtyIds
        : undefined
      this.listStationsQuery.withoutSpecialty = filter.withoutSpecialty ? 'true' : undefined
      this.setQueryString()
      await this.getListStations()
    },
    async setHoursFilter(startHour: string, endHour: string) {
      const startHours = this.listStationsQuery.startHour
        ? this.listStationsQuery.startHour.split(',')
        : []
      const endHours = this.listStationsQuery.endHour
        ? this.listStationsQuery.endHour.split(',')
        : []

      const exists = startHours.some(
        (hour, index) => hour === startHour && endHours[index] === endHour
      )

      if (!exists) {
        startHours.push(startHour)
        endHours.push(endHour)

        this.listStationsQuery.startHour = startHours.join(',')
        this.listStationsQuery.endHour = endHours.join(',')

        this.setQueryString()
        await this.getListStations()
      }
    },
    async removeHoursFilter(startHour: string, endHour: string) {
      const startHours = this.listStationsQuery.startHour
        ? this.listStationsQuery.startHour.split(',')
        : []
      const endHours = this.listStationsQuery.endHour
        ? this.listStationsQuery.endHour.split(',')
        : []

      const indexToRemove = startHours.findIndex(
        (hour, index) => hour === startHour && endHours[index] === endHour
      )

      if (indexToRemove !== -1) {
        startHours.splice(indexToRemove, 1)
        endHours.splice(indexToRemove, 1)

        this.listStationsQuery.startHour = startHours.length ? startHours.join(',') : undefined
        this.listStationsQuery.endHour = endHours.length ? endHours.join(',') : undefined

        this.setQueryString()
        await this.getListStations()
      }
    },
    async removeUnitFilter(unitId: string) {
      this.listStationsQuery.healthUnits = this.listStationsQuery.healthUnits?.filter(
        (id) => id != unitId
      )
      this.setQueryString()
      await this.getListStations()
    },
    async setHealthUnitFilter(id: number, operation: 'add' | 'remove' = 'add') {
      if (operation === 'add') {
        const exist =
          this.listStationsQuery.healthUnits?.find((item) => item === String(id)) || undefined
        if (exist) return
        const units = this.listStationsQuery.healthUnits ? this.listStationsQuery.healthUnits : []
        units.push(String(id))
        this.listStationsQuery.healthUnits = units
      }
      this.setQueryString()
      await this.getListStations()
    },
    async setScheduleFilter(id: number, operation: 'add' | 'remove' = 'add') {
      if (operation === 'add') {
        const exist =
          this.listStationsQuery.schedules?.find((item) => item === String(id)) || undefined
        if (exist) return
        const units = this.listStationsQuery.schedules ? this.listStationsQuery.schedules : []
        units.push(String(id))
        this.listStationsQuery.schedules = units
      }
      this.setQueryString()
      await this.getListStations()
    },
    async removeScheduleFilter(scheduleId: string) {
      this.listStationsQuery.schedules = this.listStationsQuery?.schedules?.filter(
        (id) => id != scheduleId
      )
      this.setQueryString()
      await this.getListStations()
    },
    async setFilters(filters: FilterDashboardStates) {
      this.listStationsQuery = mergeMoreFiltersIntoQuery(this.listStationsQuery, filters)
      this.setQueryString()
      await this.getListStations()
    },
    /**
     * Painel "Filtros e lentes" do celular (spec R8): o rascunho vira a próxima query pela mesma
     * lógica das actions de filtro e grava de uma vez — uma requisição, não uma por filtro.
     * `fetch: false` quando a data também mudou: o `goToDate` que vem depois busca.
     */
    async applyFilterDraft(next: ListFiltersRequest, options: { fetch?: boolean } = {}) {
      // Cópia: o painel pode continuar editando o rascunho reativo depois de aplicar.
      this.listStationsQuery = { ...next }
      this.setQueryString()
      if (options.fetch === false) return
      await this.getListStations()
    },
    removeFilter(filter: string) {
      if (!this.listStationsQuery[filter]) return
      delete this.listStationsQuery[filter]
      this.setQueryString()
    },
    /**
     * Prévia do lote (H7): `dryRun: true` — o BE não grava e devolve os mesmos contadores
     * (DECISIONS A8). Não recarrega a grade. Erro propaga: o caller cai na prévia local.
     */
    async previewBulkSectorSpecialty(
      body: BulkSectorSpecialtyRequest
    ): Promise<BulkSectorSpecialtyResponse> {
      return Network.station.bulkSectorSpecialty({ ...body, dryRun: true })
    },
    /** Setor/especialidade em lote (H7). Erro propaga para o `notifyError` do modal. */
    async applyBulkSectorSpecialty(
      body: BulkSectorSpecialtyRequest
    ): Promise<BulkSectorSpecialtyResponse> {
      // Escrita real: nunca repassa `dryRun` (o mesmo corpo pode ter vindo da prévia).
      const result = await Network.station.bulkSectorSpecialty({
        ids: body.ids,
        sector: body.sector,
        specialty: body.specialty
      })
      await this.refreshListStations()
      return result
    },
    /**
     * Mover para outra escala (H8): 1 request (`PATCH /stations/bulk/move`) para a seleção
     * toda. O BE é tudo-ou-nada: erro propaga sem recarregar (nada mudou).
     */
    async moveStations(ids: number[], targetScheduleId: number): Promise<MoveStationsResponse> {
      const result = await Network.station.moveStations(ids, targetScheduleId)
      await this.refreshListStations()
      return result
    }
  }
})

export type DoctorReportsState = {
  doctorReports: ConsultingResponse
  isLoading: boolean
  /** Erro do load do relatório dos médicos — null quando ok. */
  loadError: string | null
}

export const DOCTOR_REPORTS_LOAD_ERROR = 'Não foi possível carregar o relatório dos médicos'

let _doctorReportsFetchId = 0

export const useDoctorReportsStore = defineStore('doctorReports', {
  state: (): DoctorReportsState => ({
    isLoading: false,
    doctorReports: { rows: [], totalRecords: 0 },
    loadError: null
  }),
  actions: {
    /** @param shared ver `getListConsultingStations`. */
    async getDoctorReports(
      filters: ConsultingRequest,
      shared?: Promise<ConsultingResponse>
    ): Promise<void> {
      const fetchId = ++_doctorReportsFetchId
      try {
        this.isLoading = true
        this.loadError = null
        const data = copyConsulting(await (shared ?? Network.station.getConsultingList(filters)))
        if (fetchId === _doctorReportsFetchId) {
          this.doctorReports = data
        }
      } catch (error) {
        // fallback intencional: sem rethrow — disparada pelo Promise.allSettled de
        // updateAllStores (useFilterStation). Antes o catch devolvia lista vazia e a
        // falha era indistinguível de "relatório sem linhas"; agora o erro vira
        // estado visível (loadError) e o consumidor (DoctorReport.vue) mostra retry.
        if (fetchId === _doctorReportsFetchId) {
          this.doctorReports = { rows: [], totalRecords: 0 }
          this.loadError = getApiErrorMessage(error, DOCTOR_REPORTS_LOAD_ERROR)
        }
      } finally {
        if (fetchId === _doctorReportsFetchId) {
          this.isLoading = false
        }
      }
    }
  }
})

export type StationsApproveState = {
  stationsToApprove: StationsToApproveResponse[]
  totalRecords: number
  isLoading: boolean
  loadError: string | null
}

// Mesmo texto do fallback do StationService.getStationsToApprove — o consumidor
// deduplica para não repetir o genérico como detalhe do estado de erro.
export const STATIONS_TO_APPROVE_LOAD_ERROR =
  'Não foi possível carregar os plantões aguardando aprovação'

let _approveFetchId = 0

export const useStationsApproveStore = defineStore('stationsApprove', {
  state: (): StationsApproveState => ({
    isLoading: false,
    stationsToApprove: [],
    totalRecords: 0,
    loadError: null
  }),
  actions: {
    async getStationsToApprove(params: ConsultingRequest): Promise<void> {
      const fetchId = ++_approveFetchId
      try {
        this.isLoading = true
        this.loadError = null
        const result = await Network.station.getStationsToApprove(params)
        if (fetchId === _approveFetchId) {
          this.stationsToApprove = result.rows
          this.totalRecords = result.totalRecords
        }
      } catch (error) {
        // Estado de erro visível na view (ReportErrorState + retry) em vez de lista
        // falsamente vazia. Sem rethrow: updateAllStores usa allSettled e a view lê loadError.
        if (fetchId === _approveFetchId) {
          this.stationsToApprove = []
          this.totalRecords = 0
          this.loadError = getApiErrorMessage(error, STATIONS_TO_APPROVE_LOAD_ERROR)
        }
      } finally {
        if (fetchId === _approveFetchId) {
          this.isLoading = false
        }
      }
    }
  }
})
